import { useEffect, useState, useMemo } from "react";
import { MessageCircle, Sparkles, FileText, Newspaper, Video, Mail, Inbox, ChevronLeft, ChevronRight, Check, RotateCcw, X, Loader2, Send, Phone, CalendarClock } from "lucide-react";
import { useActiveCohort, withCohort } from "@/lib/cohort-context";

// Mirror server-side enum from kavya.ts. Plain-English labels here so the
// investor sees a real reason — never expose machine codes in UI.
const REOPEN_REASON_OPTIONS: { value: string; label: string; help: string }[] = [
  { value: "didnt_answer", label: "Didn't answer my question", help: "The note we got back didn't address what I actually asked." },
  { value: "still_confused", label: "Still confused", help: "I read it, but I don't fully understand the resolution." },
  { value: "need_more_detail", label: "Need more detail", help: "Direction's right — I need numbers, names, or context." },
  { value: "wrong_information", label: "Wrong information", help: "Something in the response doesn't match what I know." },
  { value: "not_relevant_anymore", label: "Not relevant anymore", help: "Situation has changed — close-out doesn't fit the current ask." },
];

type MyTask = {
  id: number;
  title: string;
  status: string;
  closedAt: string | null;
  resolutionNote: string | null;
  reopenCount: number | null;
  lastReopenAt: string | null;
  lastReopenReason: string | null;
};

type Notification = {
  id: number;
  category: string;
  agentSource: string | null;
  title: string;
  body: string;
  relatedCompanyId: number | null;
  link: string | null;
  read: boolean;
  createdAt: string;
};

type CalendarData = {
  byDate: Record<string, { total: number; unread: number; categories: string[] }>;
  totalUnread: number;
  total: number;
};

const CAT_META: Record<string, { label: string; color: string; Icon: any }> = {
  kavya_chat: { label: "Kavya", color: "#FF5C00", Icon: MessageCircle },
  digest: { label: "Daily Digest", color: "#FF5C00", Icon: Mail },
  meera_research: { label: "Meera", color: "#10B981", Icon: Sparkles },
  riya_media: { label: "Riya", color: "#A855F7", Icon: Newspaper },
  priya_editorial: { label: "Priya", color: "#F43F5E", Icon: FileText },
  chetan_video: { label: "Chetan", color: "#3B82F6", Icon: Video },
  call: { label: "Calls", color: "#FF5C00", Icon: Phone },
  proposal: { label: "Proposed times", color: "#FBBF24", Icon: CalendarClock },
  system: { label: "System", color: "#9CA3AF", Icon: Inbox },
};

function catMeta(cat: string) {
  return CAT_META[cat] || CAT_META.system;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function dateKey(d: Date) {
  return d.toISOString().substring(0, 10);
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function daysInMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export default function InvestorPortalInbox() {
  const [calendar, setCalendar] = useState<CalendarData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [myTasks, setMyTasks] = useState<MyTask[]>([]);
  const [reopenTask, setReopenTask] = useState<MyTask | null>(null);
  const [reopenReason, setReopenReason] = useState<string>("");
  const [reopenNote, setReopenNote] = useState("");
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [outreach, setOutreach] = useState<any[]>([]);
  const [openOutreachId, setOpenOutreachId] = useState<number | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyBusy, setReplyBusy] = useState<number | null>(null);
  const [replyError, setReplyError] = useState<Record<number, string>>({});

  const token = typeof window !== "undefined" ? localStorage.getItem("investor_token") : null;
  const BASE = import.meta.env.BASE_URL;
  const { cohortId } = useActiveCohort();

  const fetchCalendar = async () => {
    if (!token) return;
    try {
      const r = await fetch(withCohort(`${BASE}api/investor-portal/notifications/calendar`, cohortId), {
        headers: { "x-investor-token": token },
      });
      if (r.ok) setCalendar(await r.json());
    } catch (e) { console.error(e); }
  };

  const fetchNotifications = async (date: string | null) => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const baseUrl = `${BASE}api/investor-portal/notifications${date ? `?date=${date}` : ""}`;
      const url = withCohort(baseUrl, cohortId);
      const r = await fetch(url, { headers: { "x-investor-token": token } });
      if (r.ok) {
        const data = await r.json();
        setNotifications(data.notifications || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchMyTasks = async () => {
    if (!token) return;
    try {
      const r = await fetch(withCohort(`${BASE}api/investor-portal/my-tasks`, cohortId), {
        headers: { "x-investor-token": token },
      });
      if (r.ok) {
        const data = await r.json();
        setMyTasks(data.tasks || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchOutreach = async () => {
    if (!token) return;
    try {
      const r = await fetch(`${BASE}api/investor-portal/outreach`, { headers: { "x-investor-token": token } });
      if (r.ok) setOutreach(await r.json());
    } catch (e) { console.error(e); }
  };

  const submitReply = async (id: number) => {
    const body = (replyDrafts[id] || "").trim();
    if (!body || !token) return;
    setReplyBusy(id); setReplyError(prev => ({ ...prev, [id]: "" }));
    try {
      const r = await fetch(`${BASE}api/investor-portal/outreach/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({ body }),
      });
      if (r.ok) {
        setReplyDrafts(prev => ({ ...prev, [id]: "" }));
        await fetchOutreach();
      } else {
        const e = await r.json().catch(() => ({}));
        setReplyError(prev => ({ ...prev, [id]: e.error || "Could not send" }));
      }
    } catch { setReplyError(prev => ({ ...prev, [id]: "Network error" })); }
    setReplyBusy(null);
  };

  useEffect(() => {
    fetchCalendar();
    fetchNotifications(selectedDate);
    fetchMyTasks();
    fetchOutreach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  const submitReopen = async () => {
    if (!reopenTask || !reopenReason || !token) return;
    setReopening(true); setReopenError(null);
    try {
      const r = await fetch(`${BASE}api/investor-portal/tasks/${reopenTask.id}/reopen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({ reason: reopenReason, note: reopenNote.trim() || undefined }),
      });
      if (r.ok) {
        setReopenTask(null);
        setReopenReason("");
        setReopenNote("");
        await fetchMyTasks();
      } else {
        const e = await r.json().catch(() => ({}));
        setReopenError(e.error || "Could not reopen");
      }
    } catch { setReopenError("Network error"); }
    setReopening(false);
  };

  const markRead = async (id: number) => {
    if (!token) return;
    try {
      await fetch(`${BASE}api/investor-portal/notifications/${id}/read`, {
        method: "POST",
        headers: { "x-investor-token": token },
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      fetchCalendar();
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    if (!token) return;
    try {
      await fetch(`${BASE}api/investor-portal/notifications/read-all`, {
        method: "POST",
        headers: { "x-investor-token": token },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      fetchCalendar();
    } catch (e) { console.error(e); }
  };

  const selectDate = (date: string | null) => {
    setSelectedDate(date);
    setExpandedId(null);
    fetchNotifications(date);
  };

  const grid = useMemo(() => {
    const first = startOfMonth(viewMonth);
    const firstDow = first.getDay();
    const totalDays = daysInMonth(viewMonth);
    const cells: { date: Date | null; key: string | null }[] = [];
    for (let i = 0; i < firstDow; i++) cells.push({ date: null, key: null });
    for (let i = 1; i <= totalDays; i++) {
      const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i);
      cells.push({ date: d, key: dateKey(d) });
    }
    return cells;
  }, [viewMonth]);

  const monthLabel = viewMonth.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const today = dateKey(new Date());

  return (
    <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold text-white">Inbox</h1>
            <p className="text-white/40 text-sm mt-1">
              Your conversations with Kavya and updates from our agents — organised by day.
            </p>
          </div>
          {calendar && calendar.totalUnread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-white/60 hover:text-white px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 flex items-center gap-2"
            >
              <Check className="w-3 h-3" /> Mark all as read ({calendar.totalUnread})
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          <div className="bg-[#111111] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
                className="p-1.5 rounded hover:bg-white/5 text-white/60"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-sm font-medium text-white">{monthLabel}</div>
              <button
                onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
                className="p-1.5 rounded hover:bg-white/5 text-white/60"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-[10px] text-white/30 uppercase tracking-wider mb-2">
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                <div key={i} className="text-center py-1">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {grid.map((cell, i) => {
                if (!cell.date || !cell.key) {
                  return <div key={i} className="aspect-square" />;
                }
                const info = calendar?.byDate[cell.key];
                const hasActivity = !!info && info.total > 0;
                const hasUnread = !!info && info.unread > 0;
                const isSelected = selectedDate === cell.key;
                const isToday = cell.key === today;
                return (
                  <button
                    key={i}
                    onClick={() => selectDate(isSelected ? null : cell.key)}
                    className={`aspect-square relative flex flex-col items-center justify-center rounded-md text-xs transition-colors ${
                      isSelected
                        ? "bg-[#FF5C00] text-white"
                        : hasActivity
                        ? "bg-white/5 text-white hover:bg-white/10"
                        : "text-white/30 hover:bg-white/5"
                    } ${isToday && !isSelected ? "ring-1 ring-white/20" : ""}`}
                  >
                    <span>{cell.date.getDate()}</span>
                    {hasActivity && (
                      <div className="absolute bottom-1 flex gap-0.5">
                        {(info?.categories || []).slice(0, 3).map((c, j) => (
                          <div
                            key={j}
                            className="w-1 h-1 rounded-full"
                            style={{ background: isSelected ? "white" : catMeta(c).color }}
                          />
                        ))}
                      </div>
                    )}
                    {hasUnread && !isSelected && (
                      <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-2">
              <button
                onClick={() => selectDate(null)}
                className={`w-full text-left text-xs px-3 py-2 rounded-md transition-colors ${
                  selectedDate === null ? "bg-white/10 text-white" : "text-white/50 hover:bg-white/5 hover:text-white/80"
                }`}
              >
                Show all notifications
              </button>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-3 mb-1">Legend</div>
              <div className="space-y-1.5">
                {Object.entries(CAT_META).filter(([k]) => k !== "system").map(([k, m]) => (
                  <div key={k} className="flex items-center gap-2 text-[11px] text-white/50">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                    <m.Icon className="w-3 h-3" /> {m.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
          {outreach.length > 0 && (
            <div className="bg-[#111111] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Send className="w-4 h-4 text-[#FF5C00]" />
                <div>
                  <div className="text-sm font-medium text-white">From your advisor</div>
                  <div className="text-[11px] text-white/40 mt-0.5">Direct messages from your desk. Reply here — your advisor sees it instantly.</div>
                </div>
              </div>
              <div className="space-y-2">
                {outreach.map((o: any) => {
                  const isOpen = openOutreachId === o.id;
                  const lastMsg = o.messages?.[o.messages.length - 1];
                  const unrepliedFromAdvisor = lastMsg && lastMsg.senderRole !== "investor";
                  return (
                    <div key={o.id} className={`rounded-lg border ${unrepliedFromAdvisor ? "border-[#FF5C00]/25 bg-[#FF5C00]/[0.04]" : "border-white/[0.06] bg-white/[0.02]"}`}>
                      <button onClick={() => setOpenOutreachId(isOpen ? null : o.id)} className="w-full text-left p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider font-mono text-[#FF5C00]">{o.advisor?.name || "Advisor"}</span>
                          <span className="text-[10px] text-white/30">·</span>
                          <span className="text-[10px] text-white/40">{fmtDate(o.sentAt || o.repliedAt || lastMsg?.createdAt)}</span>
                          {unrepliedFromAdvisor && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />}
                        </div>
                        <div className="text-sm text-white font-medium truncate">{o.subject}</div>
                        {!isOpen && lastMsg && <div className="text-xs text-white/45 mt-1 line-clamp-2">{lastMsg.body}</div>}
                      </button>
                      {isOpen && (
                        <div className="border-t border-white/[0.06] p-3 space-y-2">
                          {(o.messages || []).map((m: any) => (
                            <div key={m.id} className={`rounded-lg p-3 border ${m.senderRole === "investor" ? "bg-white/[0.03] border-white/[0.08]" : "bg-[#FF5C00]/[0.05] border-[#FF5C00]/15"}`}>
                              <p className="text-[10px] uppercase tracking-wider font-mono text-white/45 mb-1">
                                {m.senderRole === "investor" ? "You" : m.senderRole === "kavya" ? "Kavya (nudge)" : (o.advisor?.name || "Advisor")} · {fmtDate(m.createdAt)}
                              </p>
                              <p className="text-white/85 text-sm whitespace-pre-wrap">{m.body}</p>
                            </div>
                          ))}
                          <div className="pt-2">
                            {replyError[o.id] && <div className="text-red-400 text-xs mb-2">{replyError[o.id]}</div>}
                            <textarea
                              value={replyDrafts[o.id] || ""}
                              onChange={(e) => setReplyDrafts(prev => ({ ...prev, [o.id]: e.target.value }))}
                              rows={3} maxLength={4000}
                              placeholder="Reply to your advisor…"
                              className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35 resize-none"
                            />
                            <div className="flex justify-end mt-2">
                              <button onClick={() => submitReply(o.id)}
                                disabled={replyBusy === o.id || !(replyDrafts[o.id] || "").trim()}
                                className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                                {replyBusy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                Send reply
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {myTasks.length > 0 && (
            <div className="bg-[#111111] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-white">Recent resolutions</div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    Tasks the desk closed for you. If a resolution didn't actually answer your question, reopen it — the same advisor will see it as high priority.
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {myTasks.slice(0, 5).map(t => {
                  const isReopened = t.status === "reopened" || (t.reopenCount || 0) > 0;
                  return (
                    <div key={t.id} className={`p-3 rounded-lg border ${isReopened ? "border-amber-500/25 bg-amber-500/[0.04]" : "border-white/[0.06] bg-white/[0.02]"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium truncate">{t.title}</div>
                          {t.resolutionNote && (
                            <div className="text-xs text-white/55 mt-1 italic">"{t.resolutionNote}"</div>
                          )}
                          <div className="text-[10px] text-white/35 font-mono mt-1.5">
                            {t.status === "reopened"
                              ? `reopened ${t.lastReopenAt ? fmtDate(t.lastReopenAt) : ""} · awaiting advisor`
                              : `closed ${t.closedAt ? fmtDate(t.closedAt) : ""}`}
                            {(t.reopenCount || 0) > 1 && ` · ${t.reopenCount} reopens`}
                          </div>
                        </div>
                        {t.status === "closed" && (
                          <button onClick={() => { setReopenTask(t); setReopenReason(""); setReopenNote(""); setReopenError(null); }}
                            className="text-xs text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-500/50 rounded-md px-2.5 py-1 flex items-center gap-1.5 flex-shrink-0">
                            <RotateCcw className="w-3 h-3" /> Reopen
                          </button>
                        )}
                        {t.status === "reopened" && (
                          <span className="text-[10px] uppercase tracking-wider font-mono text-amber-300 bg-amber-500/15 px-2 py-1 rounded">Reopened</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-[#111111] border border-white/[0.06] rounded-xl p-5 min-h-[400px]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-medium text-white">
                  {selectedDate
                    ? new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                    : "All notifications"}
                </div>
                <div className="text-[11px] text-white/40 mt-0.5">
                  {loading ? "Loading…" : `${notifications.length} item${notifications.length === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-white/[0.03] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Inbox className="w-10 h-10 text-white/20 mb-3" />
                <div className="text-sm text-white/50">
                  {selectedDate ? "No notifications on this day." : "No notifications yet."}
                </div>
                <div className="text-xs text-white/30 mt-1">
                  Chat with Kavya or wait for your next daily digest.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((n) => {
                  const meta = catMeta(n.category);
                  const isExpanded = expandedId === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        setExpandedId(isExpanded ? null : n.id);
                        if (!n.read) markRead(n.id);
                      }}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        n.read
                          ? "border-white/[0.06] bg-transparent hover:bg-white/[0.02]"
                          : "border-[#FF5C00]/20 bg-[#FF5C00]/[0.04] hover:bg-[#FF5C00]/[0.07]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{ background: `${meta.color}20`, color: meta.color }}
                        >
                          <meta.Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: meta.color }}>
                              {meta.label}
                            </span>
                            <span className="text-[10px] text-white/30">•</span>
                            <span className="text-[10px] text-white/40">{fmtDate(n.createdAt)}</span>
                            {!n.read && (
                              <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
                            )}
                          </div>
                          <div className={`text-sm ${n.read ? "text-white/70" : "text-white font-medium"} line-clamp-2`}>
                            {n.title}
                          </div>
                          {isExpanded && (
                            <div className="mt-3 text-sm text-white/60 whitespace-pre-wrap leading-relaxed">
                              {n.body}
                            </div>
                          )}
                          {!isExpanded && n.body && (
                            <div className="text-xs text-white/40 mt-1 line-clamp-1">{n.body}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>

      {reopenTask && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !reopening && setReopenTask(null)}>
          <div className="force-dark-surface bg-[#0E0E0E] border border-white/15 rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">Reopen this task</h3>
                <p className="text-white/55 text-sm mt-1">"{reopenTask.title}"</p>
                {reopenTask.resolutionNote && (
                  <p className="text-white/45 text-xs mt-2 italic border-l-2 border-white/15 pl-2">Advisor wrote: "{reopenTask.resolutionNote}"</p>
                )}
              </div>
              <button onClick={() => !reopening && setReopenTask(null)} className="text-white/40 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            {reopenError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs my-3">{reopenError}</div>}
            <div className="space-y-2 mt-4">
              {REOPEN_REASON_OPTIONS.map(r => (
                <label key={r.value} className={`block p-3 rounded-lg border cursor-pointer transition-colors ${reopenReason === r.value ? "border-[#FF5C00]/50 bg-[#FF5C00]/[0.06]" : "border-white/10 hover:border-white/20 hover:bg-white/[0.03]"}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="reopen-reason" value={r.value} checked={reopenReason === r.value}
                      onChange={() => setReopenReason(r.value)} className="mt-0.5 accent-[#FF5C00]" />
                    <div>
                      <div className="text-white text-sm font-medium">{r.label}</div>
                      <div className="text-white/50 text-xs mt-0.5">{r.help}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <textarea
              value={reopenNote}
              onChange={e => setReopenNote(e.target.value)}
              placeholder="What's still missing? (optional, helps your advisor)"
              rows={2}
              maxLength={500}
              className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35 resize-none mt-3"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-white/35 text-[11px] font-mono">{reopenNote.length}/500</span>
              <div className="flex gap-2">
                <button onClick={() => setReopenTask(null)} disabled={reopening}
                  className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06] disabled:opacity-50">Cancel</button>
                <button onClick={submitReopen} disabled={reopening || !reopenReason}
                  className="bg-amber-500/20 hover:bg-amber-500/30 disabled:opacity-50 text-amber-200 border border-amber-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                  {reopening ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reopening</> : <><RotateCcw className="w-3.5 h-3.5" /> Reopen task</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
